import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, Loader2 } from "lucide-react";

// Diagnóstico SÓLO LECTURA de integridad de paradas (admin). No modifica datos.
export default function RutaIntegridadDialog({ rutaId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = async () => {
    setOpen(true); setLoading(true);
    try {
      const { data: d } = await api.get(`/rutas/${rutaId}/integridad`);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo obtener el diagnóstico");
      setOpen(false);
    } finally { setLoading(false); }
  };

  const inf = data?.informe || {};
  const repL = Object.keys(inf.lineas_repetidas || {});
  const repO = Object.keys(inf.orden_repetido || {});

  return (
    <>
      <Button onClick={cargar} variant="outline" size="sm" data-testid="ruta-integridad-btn"
        className="bg-transparent border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs">
        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Integridad paradas
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ruta-integridad-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {data?.anomalias ? <ShieldAlert className="w-5 h-5 text-red-400" /> : <ShieldCheck className="w-5 h-5 text-emerald-400" />}
              Integridad · {data?.codigo || ""}
            </DialogTitle>
          </DialogHeader>
          {loading && <div className="flex items-center gap-2 text-sm text-zinc-400"><Loader2 className="w-4 h-4 animate-spin" /> Analizando…</div>}
          {data && !loading && (
            <div className="space-y-3 text-sm">
              <div className={`rounded border p-2 ${data.anomalias ? "border-red-500/40 bg-red-500/10 text-red-200" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"}`} data-testid="integridad-resumen">
                {data.anomalias
                  ? <>Anomalías: {repL.length > 0 && <span>{repL.length} línea(s) repetida(s) · </span>}{repO.length > 0 && <span>nº parada repetido ({repO.map((k) => `#${k}`).join(", ")}) · </span>}{Object.keys(inf.componentes_repetidos || {}).length > 0 && <span>componentes en varias paradas · </span>}{!inf.orden_secuencial && <span>orden no secuencial</span>}</>
                  : <>Sin duplicados: {inf.n_paradas} paradas con línea única y orden 1..{inf.n_paradas}. Hoja de carga: {data.n_componentes_hoja_carga} componentes.</>}
              </div>
              <div className="text-xs text-zinc-400">Creada por <b className="text-zinc-200">{data.created_by || "—"}</b> · {data.created_at?.slice(0, 16)} · origen probable: {data.origen_probable}{data.integridad_normalizada && <> · normalizada ({data.integridad_normalizada.origen})</>}</div>
              {Object.keys(data.espacios_con_varias_lineas || {}).length > 0 && (
                <div className="text-xs text-amber-200/80" data-testid="integridad-espacios-multi">
                  Espacios con varias líneas DISTINTAS (válido): {Object.values(data.espacios_con_varias_lineas).map((v) => v.length).join(", ")} líneas
                </div>
              )}
              <table className="w-full text-xs">
                <thead className="text-zinc-500 text-left"><tr><th className="py-1">#</th><th>Espacio</th><th>Petición</th><th>Línea</th><th>Comp. parada</th><th>Comp. línea BD</th><th>Cierre</th></tr></thead>
                <tbody>
                  {data.paradas.map((p) => {
                    const dup = repL.includes(p.linea_id) || repO.includes(String(p.orden));
                    return (
                      <tr key={p.idx} className={`border-t border-zinc-900 ${dup ? "bg-red-500/10 text-red-200" : ""}`} data-testid={`integridad-parada-${p.idx}`}>
                        <td className="py-1 font-mono">{p.orden}</td>
                        <td className="truncate max-w-[180px]" title={p.espacio}>{p.espacio || "—"}{p.es_revisita && <span className="ml-1 text-[9px] text-violet-300">revisita</span>}</td>
                        <td className="font-mono">{p.peticion_codigo || (p.es_manual ? "manual" : "—")}</td>
                        <td className="font-mono" title={p.linea_id}>{p.linea_id ? p.linea_id.slice(0, 8) : "—"}{!p.linea_existe && p.linea_id && <span className="text-red-300"> ✗</span>}</td>
                        <td className="text-center">{p.componentes_incluidos.length}</td>
                        <td className="text-center">{p.n_componentes_linea_bd}</td>
                        <td>{p.estado_cierre || (p.reseteada ? "reseteada" : "—")}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div className="text-[11px] text-zinc-500">Una parada con varios componentes aparece UNA vez aquí y con N filas en la hoja de carga del PDF (una por pieza). Este diagnóstico no modifica ningún dato.</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
